import type { MouseEventHandler } from 'react';

import ActionButton from 'components/ActionButton';
import Img from 'components/Img';
import Link from 'components/Link';
import Price, { BasePriceText } from 'components/Price';
import ProductTag from 'components/ProductTag';
import Rating from 'components/Rating';
import Text from 'components/Text';
import type { VariantStockResponse } from 'models/api';
import type { ProductPrice } from 'models/price';
import type { ProductTag as ProductTagModel, VariantId } from 'models/product';
import { cn } from 'utils/classNames';
import { useI18n } from 'utils/i18n';

import { useProductCardScreenReaderText } from './helpers';
import ProductCardStockInfo, { type StockInfo } from './ProductCardStockInfo';
import ProductCardTags from './ProductCardTags';
import ProductCardTechnicalInfo from './ProductCardTechnicalInfo';

export interface Props {
	addToCartDisabled?: boolean;

	addToWishlistDisabled?: boolean;

	/** Base price if the main price has a discount */
	basePrice?: ProductPrice;

	/** Extra container class names */
	className?: string;

	/** Energy Symbol */
	energySymbolAlt?: string;

	/** Energy Symbol */
	energySymbolSrc?: string;

	/** Product tag in the Flag section */
	flag?: ProductTagModel;

	/** The heading in the component */
	heading?: string;

	/** The link to the product page */
	href: string;

	/** Image alt text */
	imageAlt: string | undefined;

	/** Image src attribute */
	imageSrc: string | undefined;

	/** Stock object for stock section stock info */
	legacyStockInfo: StockInfo | undefined;

	/** Add to cart callback */
	onAddToCartClick?: MouseEventHandler<HTMLButtonElement>;

	/** Add to wishlist callback */
	onAddToWishlistClick?: MouseEventHandler<HTMLButtonElement>;

	/** Link click callback */
	onLinkClick?: MouseEventHandler<HTMLAnchorElement>;

	/** The main price to display */
	price: ProductPrice | undefined;

	/** Link to the product sheet */
	productSheetLink?: string;

	/** Link text for the product sheet link */
	productSheetLinkDescription?: string;

	/** Sets the number of reviews */
	reviews?: number;

	/** Sets the numbers of score/stars */
	score?: number;

	/** Show and hide options for the button */
	showAddToCartButton?: boolean;

	/** Show and hide options for the button */
	showAddToWishlistButton?: boolean;

	/** Screen reader subheading as the visible might include nonsense characters */
	srSubheading?: string;

	stock?: VariantStockResponse;

	/** Display information related to store */
	storesEnabled: boolean;

	/** The sub heading in the component */
	subheading?: string;

	/** Product tags in the Product or Price sections */
	tags?: ProductTagModel[];

	/** The product's unique ID */
	variantId: VariantId;

	/** Vat info price text */
	vatPriceText?: string;
}

export default function ProductCardHorizontal({
	addToCartDisabled,
	addToWishlistDisabled,
	basePrice,
	className,
	energySymbolAlt = '',
	energySymbolSrc,
	flag,
	heading,
	href,
	imageAlt,
	imageSrc,
	legacyStockInfo,
	onAddToCartClick,
	onAddToWishlistClick,
	onLinkClick,
	price,
	productSheetLink,
	productSheetLinkDescription,
	reviews,
	score = 0,
	showAddToCartButton = false,
	showAddToWishlistButton = false,
	srSubheading,
	stock,
	storesEnabled,
	subheading,
	tags,
	variantId,
	vatPriceText,
}: Props) {
	const { t } = useI18n();
	const screenReaderText = useProductCardScreenReaderText({
		basePrice,
		flag,
		heading,
		price,
		reviewCount: reviews,
		score,
		srSubheading,
		subheading,
		tags,
		vatPriceText,
	});

	const hasReviews = score > 0;

	return (
		<article
			className={cn(
				className,
				// The screen reader friendly linking results in a pretty wonky grid
				// layout, where the link is stacked on top of other content.
				'relative grid w-full max-w-120',
				// First column is the image size + margin.
				'grid-cols-[7rem_minmax(0,1fr)_minmax(0,1fr)]',
				// Using custom rule with min-content to have the rows collapse.
				'grid-rows-[repeat(3,minmax(0,min-content))]',
			)}
			aria-label={heading}
		>
			{/* Main content link. Have a proper link text as content just to be fully correct. */}
			<Link
				href={href}
				onClick={onLinkClick}
				className="peer relative z-2 col-span-2 col-start-2 row-span-1 row-start-1 overflow-hidden indent-[110%] whitespace-nowrap"
				aria-label={screenReaderText}
			>
				{heading}
			</Link>
			{/* Price link. */}
			<Link
				href={href}
				onClick={onLinkClick}
				className="peer relative z-1 col-span-2 col-start-1 row-span-2 row-start-1"
				isScreenReaderHidden
			>
				&nbsp;
			</Link>
			<div className="relative col-start-1 row-span-3 row-start-1" aria-hidden>
				{flag && (
					<ProductTag
						type={flag.type}
						text={flag.text}
						size="sm"
						className="absolute top-0 left-0 z-1"
					/>
				)}
				<Img
					src={imageSrc}
					alt={imageAlt}
					width={96}
					height={96}
					className="size-24 object-contain"
				/>
			</div>

			<div
				className="col-span-2 col-start-2 row-start-1 peer-hover:**:data-heading:underline"
				aria-hidden
			>
				{tags && <ProductCardTags tags={tags} className="mb-2" />}
				<header className={cn('relative', !hasReviews && 'mb-4')}>
					{heading && (
						<Text as="p" className="font-bold" data-heading>
							{heading}
						</Text>
					)}
					{subheading && (
						<>
							{srSubheading && <p className="sr-only">{srSubheading}</p>}
							<Text as="pSmall" aria-hidden={srSubheading ? 'true' : undefined}>
								{subheading}
							</Text>
						</>
					)}
				</header>

				{hasReviews && (
					<Rating
						size="small"
						displayMode="block"
						className="mt-2"
						score={score}
						reviewCount={reviews}
						aria-hidden
					/>
				)}
			</div>

			{(showAddToCartButton || price) && (
				<div className="col-span-2 col-start-2 row-start-2 mt-2 flex items-center">
					{price && (
						<div aria-hidden>
							<Price price={price} size="sm" />
							<Text text={vatPriceText} as="pXSmall" />
							<BasePriceText
								basePrice={basePrice}
								displayedPriceType={price.priceType}
								recentLowestPriceKey="product_list_recent_lowest_price_text"
								className="mt-1 text-xs"
							/>
						</div>
					)}
					{showAddToWishlistButton && onAddToWishlistClick && (
						<ActionButton
							className="ml-auto"
							size="sm"
							variant="secondary"
							leadingIcon="shopping-list"
							hasVisibleText={false}
							disabled={addToWishlistDisabled}
							onClick={onAddToWishlistClick}
							minimunLoadingTime={1000}
						>
							{t('general_add_to_wishlist_button_text')}
						</ActionButton>
					)}
					{showAddToCartButton && onAddToCartClick && (
						<ActionButton
							className={showAddToWishlistButton ? 'ml-2' : 'ml-auto'}
							size="sm"
							variant="cta"
							leadingIcon="cart"
							hasVisibleText={false}
							disabled={addToCartDisabled}
							onClick={onAddToCartClick}
							minimunLoadingTime={1000}
						>
							{t('general_add_to_cart_button_text')}
						</ActionButton>
					)}
				</div>
			)}

			<ProductCardTechnicalInfo
				className="col-span-2 col-start-2 row-start-3 mt-4"
				energySymbolAlt={energySymbolAlt}
				energySymbolSrc={energySymbolSrc}
				href={href}
				onLinkClick={onLinkClick}
				productSheetLink={productSheetLink}
				productSheetLinkDescription={productSheetLinkDescription}
			/>

			<ProductCardStockInfo
				storesEnabled={storesEnabled}
				stockInfo={legacyStockInfo}
				stock={stock}
				variantId={variantId}
				className="col-span-2 col-start-2 row-start-4 mt-2"
			/>
		</article>
	);
}
ProductCardHorizontal.displayName = 'ProductCardHorizontal';
